import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import {
  Navbar,
  Nav,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu
} from 'reactstrap';

import {
  AdminSidebarData,
  AuditorSidebarData,
  CrmSideBarData,
  IframeCrmSideBarData,
  IframeSidebarData,
  SidebarData,
  TenantSidebarData
} from '../sidebar/SidebarData';
import { socket } from '../../config/Socket';
import SidebarMenu from '../sidebar/SidebarMenu';
import user1 from '../../assets/images/users/user4.jpg';
import { clearPersister, languageOptions } from '../../utils/common';
import LogoutConfirmationModal from '../Modals/LogoutConfirmationModal';
import { ReactComponent as AuthLogo } from '../../assets/images/logos/Collex-white.svg';

import {
  setSidebarType,
  setTimezone,
  useCurrentSidebarType,
  useLoginUserType
} from '../../store/slices/commonSlice';
import { useBreakType, useCallingStatus, useSipStatus } from '../../store/slices/dialSlice';
import { authLogout, useUserDetails, useUserRolePermission } from '../../store/slices/authSlice';
import { useCountList } from '../../store/slices/tenantSlices/chatSlice';
import { useHelpCountList } from '../../store/slices/tenantSlices/helpDeskSlice';
import { pauseModeUpdate, setPauseMode, usePauseMode } from '../../store/slices/callingSlice';
import { useBrandingDetails } from '../../store/slices/adminSlices/brandingDetailSlice';
import { useHelpMetaCountList } from '../../store/slices/tenantSlices/metaHelpDeskSlice';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import { UncontrolledTooltip } from 'reactstrap';

const Header = ({ handleTimeReset }) => {
  const isEmbedded = window.self !== window.top;

  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useUserDetails();
  const pauseMode = usePauseMode();
  const sipStatus = useSipStatus();
  const loginUserType = useLoginUserType();
  const callStatus = useCallingStatus();
  const breakType = useBreakType();
  const currentSidebarType = useCurrentSidebarType();
  const userRolePermission = useUserRolePermission();
  const countList = useCountList();
  const helpCountList = useHelpCountList();
  const helpMetaCountList = useHelpMetaCountList();
  const brandingDetails = useBrandingDetails();
  const [menuItems, setMenuItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const network = useNetworkStatus();

  const hasPermission = (permissionKey) => {
    return userRolePermission?.some(
      (permission) => permission?.module_name === permissionKey && permission?.permission.is_view
    );
  };

  const filterSidebarItems = (items) => {
    if (!items) return [];
    return items
      .map((item) => {
        if (item?.subItems && item?.subItems?.length > 0) {
          const filteredSubItems = filterSidebarItems(item.subItems);
          if (filteredSubItems.length === 0) {
            return null;
          }
          return { ...item, subItems: filteredSubItems };
        }
        if (item?.permissionKey && !hasPermission(item.permissionKey)) {
          return null;
        }
        return item;
      })
      .filter(Boolean);
  };

  useEffect(() => {
    const hasChatPermission =
      user?.process_details?.chat_with_supervisor || user?.process_details?.chat_with_user;
    const hasHelpChatPermission = user?.userWhatsappPermission;

    const chatItem = hasChatPermission && {
      title: 'Chat',
      href: '/chat',
      i18nTitle: 'chat',
      id: 'chat',
      icon: <Icon.MessageCircle size={20} />,
      collapisble: false
    };

    const helpChatItem = hasHelpChatPermission && {
      title: 'Help Desk',
      href: '/help-desk',
      i18nTitle: 'helpDesk',
      id: 'help-desk',
      icon: <Icon.HelpCircle size={20} />,
      collapisble: false
    };

    const metaHelpChatItem = hasHelpChatPermission && {
      title: 'Meta Help Desk',
      href: '/meta-help-desk',
      id: 'meta-help-desk',
      i18nTitle: 'metaHelpDesk',
      icon: <Icon.BookOpen size={20} />,
      collapisble: false
    };

    const getAgentSidebar = () => {
      if (currentSidebarType === 'main') {
        if (isEmbedded) {
          return [...IframeSidebarData];
        } else {
          const isPreviewType =
            user?.process_details?.type === 'OutboundAndPreview' ||
            user?.process_details?.type === 'BlendedAndPreview';

          let items = [...SidebarData];
          if (!user?.cdr_permission) {
            items = items.filter((item) => item.id !== 'my-reports');
          }
          if (chatItem) items.push(chatItem);
          if (helpChatItem) items.push(helpChatItem);
          if (metaHelpChatItem) items.push(metaHelpChatItem);
          if (isPreviewType) {
            items.push({
              title: 'Lead Preview',
              href: '/lead-preview',
              i18nTitle: 'leadPreview',
              id: 'lead-preview',
              icon: <Icon.List size={20} />,
              collapisble: false
            });
          }

          return items;
        }
      } else {
        if (isEmbedded) {
          return [...IframeCrmSideBarData];
        } else {
          return [
            ...CrmSideBarData,
            chatItem,
            user?.product_manage === 0 && {
              title: 'Product',
              href: '/product',
              id: 'product',
              i18nTitle: 'product',
              icon: <Icon.Package size={20} />,
              collapisble: false
            },
            user?.process_details?.script && {
              title: 'Script',
              href: '/script',
              id: 'script',
              i18nTitle: 'script',
              icon: <Icon.FileText size={20} />,
              collapisble: false
            },
            {
              title: 'My Call History',
              href: '/live-callhistory',
              id: 'live-callhistory',
              i18nTitle: 'liveCallHistory',
              icon: <Icon.PhoneCall size={20} />,
              collapisble: false
            }
          ].filter(Boolean);
        }
      }
    };

    switch (loginUserType) {
      case 'agent':
        setMenuItems(getAgentSidebar());
        break;
      case 'MasterAdmin':
        setMenuItems(AdminSidebarData);
        break;
      case 'auditor':
        setMenuItems(AuditorSidebarData);
        break;
      default:
        setMenuItems(filterSidebarItems(TenantSidebarData));
    }
  }, [user, loginUserType, currentSidebarType, userRolePermission]);

  useEffect(() => {
    dispatch(
      setTimezone(
        user?.time_zone_details?.zone_name || process.env.REACT_APP_TIMEZONE || 'Asia/Kolkata'
      )
    );
  }, [user]);

  const onLogoutClick = async () => {
    if (breakType || callStatus) {
      toast.warn('Please stay logged in');
      setIsModalOpen(false);
      return false;
    }
    if (
      process.env.REACT_APP_AGENT_PAUSE_MODE === 'true' &&
      user?.type === 'User' &&
      (user?.process_details?.type === 'OutboundAndPredictive' ||
        user?.process_details?.type === 'BlendedAndPredictive') &&
      pauseMode === 0
    ) {
      toast.warn("Please change your status to 'pause mode'");
      setIsModalOpen(false);
      return false;
    }
    setIsLoading(true);
    try {
      window.__SIP_MANUAL_LOGOUT__ = true;
      await dispatch(authLogout()).unwrap();
      handleTimeReset && handleTimeReset();
      if (loginUserType === 'agent') {
        socket.emit('user_logout', {
          user_id: user?.user_id,
          process_id: user?.process_details?.process_id,
          company_id: user?.companyId,
          type: user?.type
        });
        socket.disconnect();
        socket.off('user_logout', {
          user_id: user?.user_id,
          process_id: user?.process_details?.process_id,
          company_id: user?.companyId,
          type: user?.type
        });
      }
      setIsLoading(false);
      setIsModalOpen(false);
      clearPersister(dispatch);
      window.__SIP_MANUAL_LOGOUT__ = true;
      localStorage.clear();
      localStorage.removeItem('timerDuration');
      localStorage.removeItem('timerStartTime');
    } catch (error) {
      setIsLoading(false);
      setIsModalOpen(false);
      console.log('error', error);
    }
  };

  const getCurrentLocation = () => {
    const path = window.location.pathname.split('/');
    return path?.length > 0 ? `/${path[1]}` : '';
  };

  const getCountForRole = (role) => {
    const roleData = countList.filter((item) => item._id.fromRole === role);
    return roleData ? roleData.length : 0; // Return 0 if no data is found for the role
  };

  const getTotalChatCount = () => {
    if (user?.process_details?.chat_with_supervisor && user?.process_details?.chat_with_user) {
      return getCountForRole('agent') + getCountForRole('supervisor');
    } else if (user?.process_details?.chat_with_user) {
      return getCountForRole('agent');
    } else if (user?.process_details?.chat_with_supervisor) {
      return getCountForRole('supervisor');
    } else {
      return 0;
    }
  };

  const onPauseModeChange = async (pause_mode) => {
    try {
      const payload = { user_id: user?.id, pause_mode: Number(pause_mode) === 1 ? 0 : 1 };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <React.Fragment>
      <Navbar
        expand="lg"
        className="shadow HorizontalTopbar p-0 bg-white border-bottom border-white border-4">
        <div className="d-flex align-items-center justify-content-between w-100 bg-primary px-4 rounded-bottom-4 p-0">
          <div className="pt-2 d-flex align-items-center gap-4">
            <div
              className="d-flex align-items-center pb-1"
              onClick={() => {
                dispatch(setSidebarType('main'));
                navigate('/dashboard');
              }}>
              {brandingDetails && brandingDetails?.headerlogo_path ? (
                <img src={brandingDetails?.headerlogo_path} alt="logo" width={100} height={40} />
              ) : (
                <AuthLogo width={100} height={40} />
              )}
            </div>
            <Nav>
              {menuItems.map((navi, index) => {
                return (
                  <SidebarMenu
                    key={navi.id}
                    className={
                      getCurrentLocation() === navi.href
                        ? 'activeLink bg-white rounded-top-4 pb-2'
                        : ''
                    }
                    isActive={getCurrentLocation() === navi.href}
                    to={navi.href}
                    title={loginUserType === 'agent' ? t(navi.i18nTitle) : navi.title}
                    suffix={
                      user?.type === 'User'
                        ? navi.title === 'Chat'
                          ? user?.process_details?.chat_with_supervisor ||
                            user?.process_details?.chat_with_user
                            ? getTotalChatCount() > 0
                              ? getTotalChatCount()
                              : navi.suffix
                            : navi.suffix
                          : navi.title === 'Help Desk'
                            ? helpCountList?.length > 0
                              ? helpCountList.length
                              : navi.suffix
                            : navi.title === 'Meta Help Desk'
                              ? helpMetaCountList?.length > 0
                                ? helpMetaCountList.length
                                : navi.suffix
                              : navi.suffix
                        : navi.suffix
                    }
                    suffixColor={navi.suffixColor}
                    icon={navi.icon}
                    collapisble={navi.collapisble}
                    subItems={navi.subItems}
                  />
                );
              })}
            </Nav>
          </div>
          <div className="d-flex align-items-center justify-content-end gap-4">
            {loginUserType === 'agent' ? (
              <UncontrolledDropdown>
                <DropdownToggle
                  caret
                  color="transparent"
                  className="border-0 shadow-none text-white">
                  {languageOptions.find((l) => l.value === i18n.language)?.label || 'English'}
                </DropdownToggle>
                <DropdownMenu>
                  {languageOptions.map((val) => (
                    <DropdownItem key={val.value} onClick={() => i18n.changeLanguage(val.value)}>
                      {val.label}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            ) : null}
            <label className="text-white text-capitalize fw-bold fs-5">
              {user.username}
              {(Number(user.web_phone) === 0 || user?.type === 'supervisor') && (
                <span
                  className={`${sipStatus === 'Register' ? 'text-success' : sipStatus === 'Un-Register' ? 'text-danger' : 'text-secondary'}  small ms-1`}>
                  ({sipStatus})
                </span>
              )}
              <span className="ms-2">
                <Icon.Wifi
                  id="network-indicator"
                  size={20}
                  className={`${network.color} cursor-help`}
                  style={{ outline: 'none', boxShadow: 'none' }}
                />
                <UncontrolledTooltip target="network-indicator">
                  Network: {network.health} <br />
                  Speed: {network.downlink ? `${network.downlink} Mbps` : 'N/A'} <br />
                  Type: {network.effectiveType} <br />
                  Latency: {network.rtt ? `${network.rtt} ms` : 'N/A'}
                </UncontrolledTooltip>
              </span>
            </label>

            {process.env.REACT_APP_AGENT_PAUSE_MODE === 'true' &&
            user?.type === 'User' &&
            (user?.process_details?.type === 'OutboundAndPredictive' ||
              user?.process_details?.type === 'BlendedAndPredictive') ? (
              <div
                className="bg-white rounded-circle p-1 cursor-pointer"
                onClick={() => onPauseModeChange(pauseMode)}>
                {pauseMode === 0 ? (
                  <Icon.PauseCircle className="text-danger fw-bolder" />
                ) : (
                  <Icon.PlayCircle className="text-danger fw-bolder" />
                )}
              </div>
            ) : null}

            <div className="position-relative">
              <img src={user1} alt="profile" className="rounded-circle" width="40" />
              <div className={`${isOnline ? 'bg-success' : 'bg-danger'} user-status`}></div>
            </div>
            {!isEmbedded && (
              <i
                className="bi bi-power fs-3 text-danger cursor-pointer"
                onClick={() => setIsModalOpen(true)}
              />
            )}
          </div>
        </div>
      </Navbar>
      <LogoutConfirmationModal
        isLoading={isLoading}
        isOpen={isModalOpen}
        onCloseClick={() => setIsModalOpen(false)}
        onLogoutClick={() => onLogoutClick()}
      />
    </React.Fragment>
  );
};

Header.propTypes = {
  handleTimeReset: PropTypes.func
};

export default Header;
